import { useEffect, useMemo, useState } from 'react';
import {
  compareSidebarParticipants,
  promoteSpeakersIntoViewport,
  reconcileSidebarOrder,
  sidebarPromotionSets,
} from '../utils/sidebarOrder';

export interface CallTileOrderInput {
  participants: any[];
  screenShareParticipantIds: ReadonlySet<string>;
  speakingIds: ReadonlySet<string>;
  handRaisedSessionIds: ReadonlySet<string>;
  isParticipantVisibleWithGrace: (id: string) => boolean;
  isParticipantInSameRoom: (id: string) => boolean;
  /**
   * How many tiles are on screen right now; speakers and raised hands beyond it are pulled into view.
   * Given the current number of tiles. Return 0 or less when it cannot be measured yet.
   */
  getViewportCapacity: (tileCount: number) => number;
}

/**
 * Who gets a call tile, in what order, for the sidebar and gallery layouts (SC-375 moved this out of
 * SidebarVideoDisplay so both share it).
 *
 * You always have a tile; others need to be in proximity (with the grace period) and in your room.
 * The base order is screen sharers, you, raised hands, unmuted, muted. Once someone has a slot they
 * keep it, so tiles don't jump around every time the speaking set changes; only a speaker or raised
 * hand who is off screen is moved into view.
 */
export function useCallTileOrder({
  participants,
  screenShareParticipantIds,
  speakingIds,
  handRaisedSessionIds,
  isParticipantVisibleWithGrace,
  isParticipantInSameRoom,
  getViewportCapacity,
}: CallTileOrderInput): string[] {
  const baseOrder = useMemo(() => {
    return participants
      .filter((p) => {
        if (p.isLocal) return true;
        return isParticipantVisibleWithGrace(p.identity) && isParticipantInSameRoom(p.identity);
      })
      .sort(compareSidebarParticipants(screenShareParticipantIds, handRaisedSessionIds))
      .map((p) => p.identity as string);
  }, [participants, screenShareParticipantIds, isParticipantVisibleWithGrace, isParticipantInSameRoom, handRaisedSessionIds]);

  const [order, setOrder] = useState<string[]>(baseOrder);

  useEffect(() => {
    setOrder((prev) => reconcileSidebarOrder(prev, baseOrder));
  }, [baseOrder]);

  useEffect(() => {
    const { pinnedIds, promoteIds } = sidebarPromotionSets(
      participants,
      screenShareParticipantIds,
      speakingIds,
      handRaisedSessionIds
    );
    setOrder((prev) => {
      const capacity = getViewportCapacity(prev.length);
      if (capacity <= 0) return prev;
      return promoteSpeakersIntoViewport(prev, promoteIds, capacity, pinnedIds);
    });
  }, [speakingIds, participants, screenShareParticipantIds, handRaisedSessionIds, getViewportCapacity]);

  return order;
}
